import { useCallback, useEffect, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { H7 } from 'foundations-components/transitional/components/H7';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { View } from 'foundations-components/transitional/components/View';
import { HR } from 'foundations-components/transitional/components/HR';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { ChirpError } from 'rpc-client-utils';
import { useLoginVerify } from '../hooks/useLoginVerify';
import { useGetUserSettingsQuery, useUpdateUserSettingsMutation, useGetPasswordStatusQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/SettingsRpc.hooks';
import { usePortalTranslations } from '../hooks/usePortalTranslations';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { usePortalsPlatformObjectId } from '../context/PortalsPlatformObjectIdContext';
import { Flex } from 'foundations-components/components/Flex';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { useIsUngated } from '../hooks/useIsUngated';
import { PasswordResetPanel } from './PasswordResetPanel';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useProfilePhotoUpload } from '../hooks/useProfilePhotoUpload';
import { ProfilePhotoSection } from './ProfilePhotoSection';
import { AccountAndProfileSection } from './AccountAndProfileSection';
import { PREFERRED_LANGUAGE_GATE, ACCOUNT_PROFILE_CRM_READ_WRITE_GATE } from '../constants/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PANEL_WIDTH_BY_BREAKPOINT = {
  mobile: '100%',
  tablet: '80%',
  desktop: 600
};
const PROFILE_PHOTO_UPLOAD_GATE = 'PortalsPlatform::Settings::ProfilePhotoUpload';
const RESET_PASSWORD_GATE = 'PortalsPlatform::Settings::ResetPassword';
function SettingsForm({
  onOpenChangePassword,
  onUnsavedChangesChange,
  alertStore
}) {
  var _passwordStatusData$h, _userSettings$profile;
  const {
    data: loginVerifyData,
    loading,
    refetch: refetchLoginVerify
  } = useLoginVerify();
  const isProfilePhotoUploadEnabled = useIsUngated(PROFILE_PHOTO_UPLOAD_GATE);
  const isResetPasswordEnabled = useIsUngated(RESET_PASSWORD_GATE);
  const isPreferredLanguageEnabled = useIsUngated(PREFERRED_LANGUAGE_GATE);
  const isCrmReadWriteEnabled = useIsUngated(ACCOUNT_PROFILE_CRM_READ_WRITE_GATE);
  const {
    data: passwordStatusData
  } = useGetPasswordStatusQuery({
    variables: {
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId()
    },
    skip: !isResetPasswordEnabled
  });
  const hasPassword = (_passwordStatusData$h = passwordStatusData === null || passwordStatusData === void 0 ? void 0 : passwordStatusData.hasPassword) !== null && _passwordStatusData$h !== void 0 ? _passwordStatusData$h : false;
  const photo = useProfilePhotoUpload(loginVerifyData, refetchLoginVerify, alertStore);
  const {
    data: translationsData
  } = usePortalTranslations();
  // Only disable on an explicit false — while loading, or on older backends that
  // don't return the flag (undefined), keep the selector interactive.
  const isLanguageDisabled = (translationsData === null || translationsData === void 0 ? void 0 : translationsData.aiTranslationsEnabled) === false;
  const [saving, setSaving] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState();
  const [languageError, setLanguageError] = useState();
  const [pendingProfile, setPendingProfile] = useState({});
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const displayLanguage = isLanguageDisabled ? translationsData === null || translationsData === void 0 ? void 0 : translationsData.domainLanguage : pendingLanguage !== null && pendingLanguage !== void 0 ? pendingLanguage : loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.preferredLanguage;
  const hasLanguageChanged = pendingLanguage !== undefined;
  const hasPendingProfileChanges = Object.keys(pendingProfile).length > 0;
  const hasUnsavedChanges = photo.hasPendingPhoto || hasLanguageChanged || hasPendingProfileChanges;
  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const objectId = usePortalsPlatformObjectId();
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const {
    data: userSettings,
    refetch: refetchUserSettings
  } = useGetUserSettingsQuery({
    variables: {
      accountId,
      portalInstanceId,
      objectId
    }
  });
  const isEditable = isCrmReadWriteEnabled && ((_userSettings$profile = userSettings === null || userSettings === void 0 ? void 0 : userSettings.profileCrmSyncEnabled) !== null && _userSettings$profile !== void 0 ? _userSettings$profile : false);
  const handleSaveChanges = useCallback(async () => {
    if (!hasUnsavedChanges) {
      return;
    }
    const hasEmptyName = ['firstName', 'lastName'].some(field => {
      var _pendingProfile$field;
      return field in pendingProfile && !((_pendingProfile$field = pendingProfile[field]) !== null && _pendingProfile$field !== void 0 && _pendingProfile$field.trim());
    });
    if (isEditable && hasEmptyName) {
      setShowRequiredErrors(true);
      return;
    }
    setSaving(true);
    setLanguageError(undefined);
    try {
      const photoSaved = await photo.save();
      if (!photoSaved) {
        return;
      }
      let hasError = false;
      if (isPreferredLanguageEnabled && !isLanguageDisabled && hasLanguageChanged && pendingLanguage) {
        try {
          await updateUserSettings({
            variables: {
              preferredLanguage: pendingLanguage,
              accountId,
              portalInstanceId,
              objectId
            }
          });
          setPendingLanguage(undefined);
          await refetchLoginVerify().catch(() => undefined);
        } catch (error) {
          var _networkError$cause, _networkError;
          hasError = true;
          const rpcError = (_networkError$cause = error === null || error === void 0 || (_networkError = error.networkError) === null || _networkError === void 0 ? void 0 : _networkError.cause) !== null && _networkError$cause !== void 0 ? _networkError$cause : error === null || error === void 0 ? void 0 : error.cause;
          const settingsError = rpcError instanceof ChirpError ? rpcError : undefined;
          if ((settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) === 'INVALID_PREFERRED_LANGUAGE') {
            setLanguageError(unescapedText('app.myAccountPopover.settings.profile.errorInvalidLanguage'));
          } else {
            setLanguageError(unescapedText('app.myAccountPopover.settings.profile.errorSaveFailed'));
          }
        }
      }
      if (isEditable && hasPendingProfileChanges) {
        try {
          await updateUserSettings({
            variables: Object.assign({}, pendingProfile, {
              accountId,
              portalInstanceId,
              objectId
            })
          });
          setPendingProfile({});
          await Promise.allSettled([refetchUserSettings(), refetchLoginVerify()]);
        } catch (error) {
          var _networkError$cause2, _networkError2;
          hasError = true;
          const rpcError = (_networkError$cause2 = error === null || error === void 0 || (_networkError2 = error.networkError) === null || _networkError2 === void 0 ? void 0 : _networkError2.cause) !== null && _networkError$cause2 !== void 0 ? _networkError$cause2 : error === null || error === void 0 ? void 0 : error.cause;
          const settingsError = rpcError instanceof ChirpError ? rpcError : undefined;
          const profileErrorText = (settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) === 'PROFILE_EDITING_DISABLED' ? unescapedText('app.myAccountPopover.settings.profile.errorEditingDisabled') : (settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) === 'FIELD_TOO_LONG' ? unescapedText('app.myAccountPopover.settings.profile.errorFieldTooLong') : unescapedText('app.myAccountPopover.settings.profile.errorSaveFailed');
          alertStore.addAlert({
            type: 'danger',
            titleText: profileErrorText,
            dismissAutoMs: 5000
          });
        }
      }
      if (!hasError) {
        alertStore.addAlert({
          type: 'success',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.saveChangesSuccess"
          }),
          dismissAutoMs: 5000
        });
      }
    } finally {
      setSaving(false);
    }
  }, [hasUnsavedChanges, photo, isPreferredLanguageEnabled, isLanguageDisabled, hasLanguageChanged, pendingLanguage, isEditable, hasPendingProfileChanges, pendingProfile, updateUserSettings, accountId, portalInstanceId, objectId, refetchLoginVerify, refetchUserSettings, alertStore]);
  return /*#__PURE__*/_jsxs(View, {
    children: [isProfilePhotoUploadEnabled && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ProfilePhotoSection, {
        loginVerifyData: loginVerifyData,
        photo: photo,
        loading: loading
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), /*#__PURE__*/_jsx(AccountAndProfileSection, {
      loginVerifyData: loginVerifyData,
      userSettings: userSettings,
      loading: loading,
      isPreferredLanguageEnabled: isPreferredLanguageEnabled,
      isEditable: isEditable,
      displayLanguage: displayLanguage,
      isLanguageDisabled: isLanguageDisabled,
      languageError: languageError,
      pendingProfile: pendingProfile,
      showRequiredErrors: showRequiredErrors,
      onLanguageChange: language => {
        setPendingLanguage(language);
        setLanguageError(undefined);
      },
      onProfileChange: (field, value) => {
        setPendingProfile(prev => Object.assign({}, prev, {
          [field]: value
        }));
      }
    }), !loginVerifyData && !loading && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "center",
        gap: "200",
        paddingBlock: "500",
        role: "alert",
        children: [/*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "heading-200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.error.title"
          })
        }), /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.error.description"
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), isResetPasswordEnabled && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        marginBlockStart: "300",
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(H5, {
          color: "text-core-default",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.security.title"
          })
        }), /*#__PURE__*/_jsx(View, {
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.security.changePasswordLabel'),
              children: /*#__PURE__*/_jsx(Flex, {
                inlineSize: "100%",
                children: /*#__PURE__*/_jsx(Button, {
                  use: "unstyled",
                  onClick: () => onOpenChangePassword(hasPassword),
                  flex: "auto",
                  children: /*#__PURE__*/_jsx(View, {
                    inlineSize: "100%",
                    borderWidth: "100",
                    borderStyle: "solid",
                    borderColor: "border-core-default",
                    borderRadius: "200",
                    backgroundColor: "fill-surface-default",
                    paddingInline: "200",
                    paddingBlock: "100",
                    children: /*#__PURE__*/_jsxs(Flex, {
                      alignItems: "center",
                      justifyContent: "space-between",
                      inlineSize: "100%",
                      gap: "200",
                      children: [/*#__PURE__*/_jsxs(Flex, {
                        alignItems: "center",
                        gap: "200",
                        children: [/*#__PURE__*/_jsx(LockedIcon, {
                          size: "300",
                          color: "text-core-default",
                          "aria-hidden": true
                        }), /*#__PURE__*/_jsx(Paragraph, {
                          color: "text-core-default",
                          marginBlock: "0",
                          typography: "body-100",
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: hasPassword ? 'app.myAccountPopover.settings.security.changePassword' : 'app.myAccountPopover.settings.security.setPassword'
                          })
                        })]
                      }), /*#__PURE__*/_jsx(RightIcon, {
                        size: "300",
                        color: "text-core-default",
                        "aria-hidden": true
                      })]
                    })
                  })
                })
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), (isProfilePhotoUploadEnabled || isPreferredLanguageEnabled || isEditable) && /*#__PURE__*/_jsx(LoadingButton, {
      use: "primary",
      block: true,
      marginBlockStart: "400",
      marginBlockEnd: "400",
      onClick: () => void handleSaveChanges(),
      loading: saving,
      disabled: saving,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.settings.saveChanges"
      })
    })]
  });
}
export function SettingsPanel({
  onClose
}) {
  const breakpoint = useBreakpoint();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [hasPasswordForPanel, setHasPasswordForPanel] = useState(false);
  const [alertStore] = useState(() => FloatingAlertStore.newFloatingAlertStore());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const isMobile = breakpoint === 'mobile';
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Panel, {
      width: PANEL_WIDTH_BY_BREAKPOINT[breakpoint],
      role: "dialog",
      "aria-labelledby": "settings-panel-heading",
      display: "flex",
      children: isMobile ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(View, {
          position: "sticky",
          insetBlockStart: "0",
          zIndex: "1",
          children: [/*#__PURE__*/_jsxs(Flex, {
            backgroundColor: "fill-surface-default",
            borderBlockEndWidth: "100",
            borderBlockEndStyle: "solid",
            borderBlockEndColor: "border-core-default",
            paddingInline: "400",
            paddingBlock: "300",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "200",
            children: [/*#__PURE__*/_jsx(H4, {
              id: isMobile ? 'settings-panel-heading' : undefined,
              color: "text-core-default",
              marginBlock: "0",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.heading"
              })
            }), /*#__PURE__*/_jsx(CloseButton, {
              autoFocus: true,
              inline: true,
              onClick: handleClose,
              color: "text-core-default"
            })]
          }), /*#__PURE__*/_jsx(View, {
            position: "relative",
            insetBlockStart: "300",
            paddingInline: "400",
            children: /*#__PURE__*/_jsx(FloatingAlertList, {
              alertStore: alertStore,
              use: "contextual"
            })
          })]
        }), /*#__PURE__*/_jsx(PanelBody, {
          tabIndex: 0,
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              onOpenChangePassword: hasPassword => {
                setHasPasswordForPanel(hasPassword);
                setShowChangePassword(true);
              },
              onUnsavedChangesChange: setHasUnsavedChanges,
              alertStore: alertStore
            })]
          })
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(View, {
          position: "sticky",
          insetBlockStart: "600",
          zIndex: "1",
          paddingInline: "1000",
          children: /*#__PURE__*/_jsx(FloatingAlertList, {
            alertStore: alertStore,
            use: "contextual"
          })
        }), /*#__PURE__*/_jsx(CloseButton, {
          autoFocus: true,
          onClick: handleClose,
          color: "text-core-default"
        }), /*#__PURE__*/_jsx(PanelBody, {
          tabIndex: 0,
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(H4, {
              id: !isMobile ? 'settings-panel-heading' : undefined,
              color: "text-core-default",
              marginBlockEnd: "200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.heading"
              })
            }), /*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              onOpenChangePassword: hasPassword => {
                setHasPasswordForPanel(hasPassword);
                setShowChangePassword(true);
              },
              onUnsavedChangesChange: setHasUnsavedChanges,
              alertStore: alertStore
            })]
          })
        })]
      })
    }), showChangePassword && /*#__PURE__*/_jsx(PasswordResetPanel, {
      hasPassword: hasPasswordForPanel,
      onClose: handleClose,
      onBack: () => setShowChangePassword(false)
    }), showDiscardConfirm && /*#__PURE__*/_jsx(ConfirmModal, {
      message: unescapedText('app.myAccountPopover.settings.discardConfirm.title'),
      description: unescapedText('app.myAccountPopover.settings.discardConfirm.description'),
      confirmLabel: unescapedText('app.myAccountPopover.settings.discardConfirm.confirm'),
      confirmUse: "danger",
      rejectLabel: unescapedText('app.myAccountPopover.settings.discardConfirm.cancel'),
      rejectUse: "secondary-ghost",
      onReject: () => setShowDiscardConfirm(false),
      onConfirm: onClose,
      size: "auto"
    })]
  });
}